import ExcelJS from "exceljs";
import {
  buildMedicalFortuneSheetData,
  downloadMedicalTablePdf,
  ensureExtension,
  triggerBlobDownload,
} from "./medicalSheetCommon";
import { CUSTOMER_TYPE } from "../constants/customer.constant";
import { getSiteDisplayName } from "../constants/siteDisplayNames";
import {
  STATUS_DROPDOWN,
  YES_NO_DROPDOWN,
  PRE_SCREENING_DROPDOWN,
  cloneAdmissionRecords,
  compareMedicalFormCustomersByName,
  customerNameFields,
  displayStatusValue,
  displayYesNoValue,
  formatAdmissionDateDisplay,
  formatMedicalFormCustomerName,
  normalizeSheetCellText,
  statusPayload,
  yesNoPayload,
} from "./medicalCustomerSheetSave";

export const TB_HEADERS = [
  "No.",
  "Customer",
  "Status",
  "Admission Date",
  "TB Form",
  "Prescreening Form (New Applicant Only)",
  "45 Days Admission Note",
  "TB Notes",
];

const BASE_COL_WIDTHS_PX = [48, 220, 80, 110, 72, 170, 140, 180];
const NO_FILL = "DD7E6B";
const NO_TEXT = "000000";
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Last Name, First Name Middle Name (Preferred Name). */
export function formatTbCustomerName(customer) {
  return formatMedicalFormCustomerName(customer);
}

export function isTbRosterCustomer(customer) {
  if (!customer) return false;
  const status = `${customer.status || ""}`.trim().toLowerCase();
  if (status !== "active") return false;
  const type = `${customer.type || ""}`.trim().toLowerCase();
  return type === CUSTOMER_TYPE.MEMBER;
}

function formatExportedOnDate(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const valid = Number.isNaN(d.getTime()) ? new Date() : d;
  return `${MONTHS[valid.getMonth()]} ${String(valid.getDate()).padStart(2, "0")}, ${valid.getFullYear()}`;
}

export function buildTbTitle(siteNum, exportedAt = new Date()) {
  const site = getSiteDisplayName(siteNum);
  return `${site} Customer TB & Prescreening Submission (Exported on ${formatExportedOnDate(
    exportedAt
  )})`;
}

function formatAdmissionDate(raw) {
  return formatAdmissionDateDisplay(raw);
}

function admissionDateFromCustomer(customer) {
  const records = customer?.admission_records;
  if (Array.isArray(records) && records.length > 0) {
    const first = `${records[0]?.admission_date ?? ""}`.trim();
    if (first) return formatAdmissionDate(first);
  }
  return formatAdmissionDate(customer?.admission_date);
}

function displayYesNo(value) {
  return displayYesNoValue(value);
}

function isFormUploaded(value) {
  return value === true || `${value ?? ""}`.trim().toLowerCase() === "true";
}

/** Yes/No from Customer Info screening field; fall back to whether the form file is uploaded. */
function submissionYesNo(yesNoValue, uploadedFlag) {
  const fromField = displayYesNo(yesNoValue);
  if (fromField) return fromField;
  return isFormUploaded(uploadedFlag) ? "Yes" : "No";
}

function statusLabel(customer) {
  return displayStatusValue(customer?.status);
}

function admissionSortValue(customer) {
  const formatted = admissionDateFromCustomer(customer);
  const match = `${formatted}`.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return 0;
  return Number(`${match[3]}${match[1]}${match[2]}`);
}

export function buildTbRows(customers = []) {
  const eligible = (customers || []).filter(isTbRosterCustomer);
  eligible.sort((a, b) => {
    const dateDiff = admissionSortValue(b) - admissionSortValue(a);
    if (dateDiff !== 0) return dateDiff;
    return compareMedicalFormCustomersByName(a, b);
  });
  return eligible.map((customer, index) => ({
    no: index + 1,
    id: `${customer?._id ?? customer?.id ?? ""}`,
    ...customerNameFields(customer),
    name: formatTbCustomerName(customer),
    status: statusLabel(customer),
    admissionDate: admissionDateFromCustomer(customer),
    admissionRecords: cloneAdmissionRecords(customer),
    tbForm: submissionYesNo(customer?.tb_screening_yes_no, customer?.tb_form),
    prescreeningForm: submissionYesNo(customer?.pre_screening_yes_no, customer?.pre_screening_form),
    fortyFiveDayNote: displayYesNo(customer?.forty_five_day_admission_note),
    tbNotes: `${customer?.tb_notes ?? ""}`.trim(),
  }));
}

export function buildTbSheetModel(rows, siteNum, exportedAt = new Date()) {
  return {
    title: buildTbTitle(siteNum, exportedAt),
    headers: TB_HEADERS,
    rows: (rows || []).map((row) => [
      `${row.no ?? ""}`,
      `${row.name ?? ""}`,
      `${row.status ?? ""}`,
      `${row.admissionDate ?? ""}`,
      `${row.tbForm ?? ""}`,
      `${row.prescreeningForm ?? ""}`,
      `${row.fortyFiveDayNote ?? ""}`,
      `${row.tbNotes ?? ""}`,
    ]),
    columnWidthsPx: BASE_COL_WIDTHS_PX,
    name: "TB",
    highlightNo: true,
    noFillColor: `#${NO_FILL}`,
    noTextColor: `#${NO_TEXT}`,
    headerRowHeightPx: 48,
    // Fortune: 0 = center, 1 = left
    halignByColumn: {
      0: 0,
      1: 1,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 1,
      7: 1,
    },
    dataVerificationByColumn: {
      2: STATUS_DROPDOWN,
      4: YES_NO_DROPDOWN,
      5: PRE_SCREENING_DROPDOWN,
      6: YES_NO_DROPDOWN,
    },
  };
}

export function buildTbFortuneSheetData(rows, siteNum, exportedAt = new Date()) {
  return buildMedicalFortuneSheetData(buildTbSheetModel(rows, siteNum, exportedAt));
}

const TB_COLUMN_COUNT = TB_HEADERS.length;

export function parseTbRowsFromWorkbook(workbook, baselineRows = []) {
  const sheetRows = workbook?.[0]?.data;
  if (!Array.isArray(sheetRows)) return baselineRows;
  const parsed = [];
  for (let i = 0; i < baselineRows.length; i += 1) {
    const row = sheetRows[i + 2] || [];
    const baseline = baselineRows[i] || {};
    const cells = [];
    for (let c = 0; c < TB_COLUMN_COUNT; c += 1) {
      cells.push(normalizeSheetCellText(row[c]));
    }
    if (!cells.some((cell) => cell)) {
      parsed.push({ ...baseline });
      continue;
    }
    parsed.push({
      ...baseline,
      no: Number(cells[0]) || baseline.no || i + 1,
      name: baseline.name || "",
      status: displayStatusValue(cells[2] || baseline.status),
      admissionDate: formatAdmissionDateDisplay(cells[3] || baseline.admissionDate),
      tbForm: displayYesNoValue(cells[4]),
      prescreeningForm: displayYesNoValue(cells[5]),
      fortyFiveDayNote: displayYesNoValue(cells[6]),
      tbNotes: cells[7] || "",
    });
  }
  return parsed;
}

export function tbRowsEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i] || {};
    const right = b[i] || {};
    const keys = ["id", "name", "status", "admissionDate", "tbForm", "prescreeningForm", "fortyFiveDayNote", "tbNotes"];
    for (const key of keys) {
      if (`${left[key] ?? ""}`.trim() !== `${right[key] ?? ""}`.trim()) return false;
    }
  }
  return true;
}

export function collectTbCustomerUpdates(draftRows = [], baselineRows = []) {
  const byId = new Map((baselineRows || []).map((row) => [`${row.id}`, row]));
  const updates = [];
  (draftRows || []).forEach((row) => {
    const id = `${row?.id ?? ""}`.trim();
    if (!id) return;
    const baseline = byId.get(id);
    if (!baseline) return;
    const payload = {};
    if (statusPayload(row.status) !== statusPayload(baseline.status)) {
      payload.status = statusPayload(row.status);
    }
    if (yesNoPayload(row.tbForm) !== yesNoPayload(baseline.tbForm)) {
      payload.tb_screening_yes_no = yesNoPayload(row.tbForm);
    }
    if (yesNoPayload(row.prescreeningForm) !== yesNoPayload(baseline.prescreeningForm)) {
      payload.pre_screening_yes_no = yesNoPayload(row.prescreeningForm);
    }
    if (yesNoPayload(row.fortyFiveDayNote) !== yesNoPayload(baseline.fortyFiveDayNote)) {
      payload.forty_five_day_admission_note = yesNoPayload(row.fortyFiveDayNote);
    }
    if (`${row.tbNotes ?? ""}`.trim() !== `${baseline.tbNotes ?? ""}`.trim()) {
      payload.tb_notes = `${row.tbNotes ?? ""}`.trim();
    }
    if (Object.keys(payload).length > 0) {
      updates.push({ customerId: id, ...payload });
    }
  });
  return updates;
}

export async function downloadTbPdf(rows, siteNum, filename = "TB_Prescreening_Submission") {
  const model = buildTbSheetModel(rows, siteNum, new Date());
  await downloadMedicalTablePdf({
    title: model.title,
    headers: model.headers,
    rows: model.rows,
    filename,
    pageOptions: {
      orientation: "landscape",
      highlightNo: true,
      noFillRgb: [221, 126, 107],
      noTextRgb: [0, 0, 0],
      fontSize: 9,
      headFontSize: 9,
      titleFontSize: 13,
      headerHeight: 36,
      lineColor: [51, 51, 51],
    },
    columnStyles: {
      0: { cellWidth: 32, halign: "center" },
      1: { cellWidth: 130, halign: "left" },
      2: { cellWidth: 52, halign: "center" },
      3: { cellWidth: 72, halign: "center" },
      4: { cellWidth: 50, halign: "center" },
      5: { cellWidth: 110, halign: "center" },
      6: { cellWidth: 90, halign: "left" },
      7: { cellWidth: 120, halign: "left" },
    },
  });
}

export async function downloadTbXlsx(rows, siteNum, filename = "TB_Prescreening_Submission") {
  const model = buildTbSheetModel(rows, siteNum, new Date());
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Worldshine";
  const sheet = workbook.addWorksheet("TB");
  const colCount = model.headers.length;
  const border80 = {
    top: { style: "thin", color: { argb: "FF333333" } },
    left: { style: "thin", color: { argb: "FF333333" } },
    bottom: { style: "thin", color: { argb: "FF333333" } },
    right: { style: "thin", color: { argb: "FF333333" } },
  };

  sheet.mergeCells(1, 1, 1, colCount);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = model.title;
  titleCell.font = { bold: true, size: 16, name: "Times New Roman" };
  titleCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  for (let col = 1; col <= colCount; col += 1) {
    sheet.getCell(1, col).border = border80;
  }
  sheet.getRow(1).height = 40;

  const headerRow = sheet.getRow(2);
  headerRow.height = 36;
  model.headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 12, name: "Arial" };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = border80;
  });

  const leftCols = new Set([1, 6, 7]);
  model.rows.forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(rowIndex + 3);
    row.forEach((value, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1);
      cell.value = value || null;
      cell.font = { size: 12, name: "Arial" };
      cell.alignment = {
        horizontal: leftCols.has(colIndex) ? "left" : "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = border80;
      if (`${value ?? ""}`.trim().toLowerCase() === "no") {
        cell.font = { size: 12, name: "Arial", color: { argb: `FF${NO_TEXT}` } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: `FF${NO_FILL}` },
        };
      }
    });
  });

  model.columnWidthsPx.forEach((px, index) => {
    sheet.getColumn(index + 1).width = Math.max(8, Math.round(px / 7));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ensureExtension(filename, ".xlsx")
  );
}
